import React from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Slider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

/**
 * ExecutionSettingsSection Component
 *
 * Section for advanced execution settings.
 *
 * @param {Object} props
 * @param {Object} props.executionData - Execution data object
 * @param {boolean} props.showSettings - Whether settings are visible
 * @param {Function} props.setShowSettings - Setter for show settings
 * @param {Function} props.handleExecutionSettingChange - Handler for setting changes
 */
function ExecutionSettingsSection({
  executionData,
  showSettings,
  setShowSettings,
  handleExecutionSettingChange,
}) {
  return (
    <>
      {/* Execution Settings Toggle */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showSettings}
              onChange={e => setShowSettings(e.target.checked)}
            />
          }
          label="Advanced Settings"
        />
      </Box>

      {/* Advanced Execution Settings */}
      {showSettings && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Execution Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" gutterBottom>
                  Temperature: {executionData.execution_settings.temperature}
                </Typography>
                <Slider
                  value={executionData.execution_settings.temperature}
                  onChange={(e, value) =>
                    handleExecutionSettingChange('temperature', value)
                  }
                  min={0}
                  max={2}
                  step={0.1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 1, label: '1' },
                    { value: 2, label: '2' },
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" gutterBottom>
                  Max Tokens: {executionData.execution_settings.max_tokens}
                </Typography>
                <Slider
                  value={executionData.execution_settings.max_tokens}
                  onChange={(e, value) =>
                    handleExecutionSettingChange('max_tokens', value)
                  }
                  min={100}
                  max={4000}
                  step={100}
                  marks={[
                    { value: 100, label: '100' },
                    { value: 1000, label: '1K' },
                    { value: 4000, label: '4K' },
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" gutterBottom>
                  Top P: {executionData.execution_settings.top_p}
                </Typography>
                <Slider
                  value={executionData.execution_settings.top_p}
                  onChange={(e, value) =>
                    handleExecutionSettingChange('top_p', value)
                  }
                  min={0}
                  max={1}
                  step={0.1}
                  marks={[
                    { value: 0, label: '0' },
                    { value: 0.5, label: '0.5' },
                    { value: 1, label: '1' },
                  ]}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="caption" gutterBottom>
                  Frequency Penalty:{' '}
                  {executionData.execution_settings.frequency_penalty}
                </Typography>
                <Slider
                  value={executionData.execution_settings.frequency_penalty}
                  onChange={(e, value) =>
                    handleExecutionSettingChange('frequency_penalty', value)
                  }
                  min={-2}
                  max={2}
                  step={0.1}
                  marks={[
                    { value: -2, label: '-2' },
                    { value: 0, label: '0' },
                    { value: 2, label: '2' },
                  ]}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
}

export default ExecutionSettingsSection;
